import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  faCircleArrowRight,
  faComment,
  faUsers,
  faSync,
  faFileInvoiceDollar,
} from '@fortawesome/pro-light-svg-icons'
import {
  FETCH_PAY_SCHEDULE_KEY,
  FETCH_PAYROLL_KEY,
  fetchAllPayrolls,
  fetchAllPaySchedules,
} from '../payrollActions'
import {
  selectPaySchedule,
  selectPreviousUnprocessedPayrollOptions,
  selectUpcomingPayroll,
} from '../payroll.selectors'
import { AutoEnrollModal } from './AutoEnrollModal'
import {
  Text,
  Button,
  Card,
  Link,
  Alert,
  Icon,
  Loader,
  CardColors,
  Select,
  Separator,
} from '@/ui'
import { UpcomingPayroll } from './UpcomingPayroll'
import { useConsumeQueryFlag, useReselector } from '@/utils/sharedHooks'
import { getIsFetching, selectIsFetchingForKeys } from '@/reducers/fetch'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { DateTime } from 'luxon'
import { clearPayrolls } from '../payroll.slice'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TypographyStyles } from '@/styles'

const PayrollHeader = () => {
  const dispatch = useAppDispatch()
  const paySchedule = useReselector(selectPaySchedule)
  const upcomingPayroll = useReselector(selectUpcomingPayroll)
  const isLoading = useReselector(selectIsFetchingForKeys, [
    FETCH_PAYROLL_KEY,
    FETCH_PAY_SCHEDULE_KEY,
  ])
  const [modalOpen, setModalOpen] = useState(false)

  const fetchPayrolls = useCallback(() => {
    dispatch(
      fetchAllPayrolls({
        end_date: DateTime.now()
          .plus({ months: 3 })
          .minus({ day: 1 })
          .toFormat(DATE_FORMATS_LUXON.GUSTO_SUBMIT),
        start_date: DateTime.now()
          .minus({ months: 3 })
          .toFormat(DATE_FORMATS_LUXON.GUSTO_SUBMIT),
      })
    )
  }, [dispatch])

  // Fetch pay schedules/payroll
  useEffect(() => {
    dispatch(fetchAllPaySchedules())
    fetchPayrolls()
  }, [dispatch, fetchPayrolls])

  const onClose = useCallback(() => {
    setModalOpen(false)
    // Changing the pay schedule will invalidate upcoming payrolls - the previous ones need to be wiped and refreshed
    dispatch(clearPayrolls())
    fetchPayrolls()
  }, [dispatch, fetchPayrolls])

  if (isLoading) {
    return (
      <div className="loading-wrapper">
        <Loader />
      </div>
    )
  }

  if (!paySchedule || !upcomingPayroll) {
    return <Alert>Something went wrong</Alert>
  }

  const isAutoPayroll = Boolean(paySchedule?.auto_payroll)
  return (
    <div className="flex flex-col gap-6">
      {!isAutoPayroll && (
        <Alert variant="default" className="p-4">
          <Text variant={TypographyStyles.H2}>Set up Automatic Payroll!</Text>
          <br />
          <Text>
            Tired of manually running payroll every time you need to pay
            someone? Set it to run automatically!
          </Text>
          <br />

          <div className="w-full md:w-1/3">
            <Button
              onClick={() => setModalOpen(true)}
              size="md"
              iconLeft={faCircleArrowRight}
            >
              Set Up Automatic Payroll
            </Button>
          </div>
        </Alert>
      )}

      <AutoEnrollModal
        modalOpen={modalOpen}
        onClose={onClose}
        paySchedule={paySchedule}
      />

      <UpcomingPayroll />
    </div>
  )
}

export const RunPayroll = () => {
  const navigate = useNavigate()
  const previousPayrollOptions = useReselector(
    selectPreviousUnprocessedPayrollOptions
  )
  const isFetching = useReselector(getIsFetching, FETCH_PAYROLL_KEY)

  const migrated = useConsumeQueryFlag('migrated')

  return (
    <div>
      <Card>
        <div className="flex flex-col gap-6">
          {/* Header Section */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <Text variant={TypographyStyles.DISPLAY_SM}>Run Payroll</Text>
            <Link
              newPage
              href="https://support.joinheard.com/hc/en-us/articles/7583457900567"
            >
              <Button variant="link" className="w-full" iconLeft={faComment}>
                I need help with this
              </Button>
            </Link>
          </div>

          <Separator />

          {migrated && (
            <Alert variant="success">
              Congrats! You’ve successfully migrated your Payroll to Heard. You
              can now run, view, and adjust payroll right on this page.
              <br />
              <Link
                href="https://www.joinheard.com/articles/how-to-set-up-payroll-for-your-therapy-practice"
                newPage
              >
                Learn how Heard Payroll works.
              </Link>
            </Alert>
          )}

          <PayrollHeader />

          <Separator />

          {/* Other Payroll Options */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <Text variant={TypographyStyles.H1}>Other Payroll Options</Text>
            <Text variant={TypographyStyles.BODY_SM}>
              Need to cancel an upcoming payroll?{' '}
              <Link href="/payroll/history">View Payroll History</Link>
            </Text>
          </div>

          {/* Options Cards */}
          <div className="flex flex-col md:flex-row gap-6">
            <Card color={CardColors.LEVEL_1} className="w-full md:w-1/3">
              <div className="flex flex-col gap-3 flex-grow">
                <Icon icon={faUsers} size="3x" color="green" />
                <Text variant={TypographyStyles.H2}>Pay Contractors</Text>
                <Text variant={TypographyStyles.BODY_SM}>
                  Run or schedule a contractor payment.
                </Text>
                <div className="flex-grow" />
                <Button
                  variant="secondary"
                  onClick={() => navigate('/payroll/run_contractor_payroll')}
                >
                  Pay Contractors
                </Button>
              </div>
            </Card>

            <Card color={CardColors.LEVEL_1} className="w-full md:w-1/3">
              <div className="flex flex-col gap-3 flex-grow">
                <Icon
                  icon={faSync}
                  size="3x"
                  color="green"
                  className="self-start"
                />
                <Text variant={TypographyStyles.H2}>Run Off-Cycle Payroll</Text>
                <Text variant={TypographyStyles.BODY_SM}>
                  Run a payroll outside your regular pay schedule.
                </Text>
                <div className="flex-grow" />
                <Button
                  variant="secondary"
                  onClick={() => navigate('/payroll/create_off_cycle_payroll')}
                >
                  Run Off-Cycle Payroll
                </Button>
              </div>
            </Card>

            <Card color={CardColors.LEVEL_1} className="w-full md:w-1/3">
              <div className="flex flex-col gap-3 flex-grow">
                <Icon
                  icon={faFileInvoiceDollar}
                  size="3x"
                  color="green"
                  className="self-start"
                />
                <Text variant={TypographyStyles.H2}>Run Previous Payrolls</Text>
                <Text variant={TypographyStyles.BODY_SM}>
                  Run a previous payroll that hasn’t been processed yet.
                </Text>
                <div className="flex-grow" />
                {isFetching ? (
                  <div className="loading-wrapper">
                    <Loader />
                  </div>
                ) : previousPayrollOptions.length ? (
                  <Select
                    placeholder="Select a pay period"
                    options={previousPayrollOptions}
                    onValueChange={(payrollUuid) =>
                      navigate(`/payroll/run_payroll/process/${payrollUuid}`)
                    }
                  />
                ) : (
                  <Text variant={TypographyStyles.BODY_SM}>
                    All payrolls Processed
                  </Text>
                )}
              </div>
            </Card>
          </div>
        </div>
      </Card>
    </div>
  )
}
